import * as THREE from 'three';
import { createCar, getCarPaintFinish, updateCarVisual } from './carFactory.js';
import { createProjectileModel, EffectsSystem } from './effects.js';
import { getCannon, getDeathExplosion, getProjectile, getTombstone } from './data.js';

const CAMERA_PRESETS = {
  wheelId:{yaw:-2.12,pitch:.28,distance:5.18},
  engineSoundId:{yaw:-2.12,pitch:.28,distance:5.18},
  cannonId:{yaw:-1.48,pitch:.3,distance:5.25},
  projectileId:{yaw:-1.35,pitch:.24,distance:4.85},
  nitroId:{yaw:-.68,pitch:.25,distance:5.35},
  explosionId:{yaw:-2.24,pitch:.3,distance:4.15},
  tombstoneId:{yaw:-2.18,pitch:.22,distance:3.05},
};

function setShadows(object){
  object.traverse((child)=>{
    if(!child.isMesh)return;
    child.castShadow=!child.userData.noShadow;
    child.receiveShadow=true;
  });
}

function createPedestal(){
  const root=new THREE.Group();
  const base=new THREE.Mesh(
    new THREE.CylinderGeometry(2.25,2.5,.18,64),
    new THREE.MeshPhysicalMaterial({color:'#242139',metalness:.38,roughness:.34,clearcoat:.48}),
  );
  base.position.y=-.09;base.receiveShadow=true;
  const top=new THREE.Mesh(
    new THREE.CylinderGeometry(2.18,2.18,.035,64),
    new THREE.MeshPhysicalMaterial({color:'#35304d',metalness:.3,roughness:.3,clearcoat:.55}),
  );
  top.position.y=.017;top.receiveShadow=true;
  const ring=new THREE.Mesh(
    new THREE.TorusGeometry(2.27,.035,8,72),
    new THREE.MeshBasicMaterial({color:'#39d9ee',transparent:true,opacity:.82}),
  );
  ring.rotation.x=Math.PI/2;ring.position.y=.025;
  const innerRing=ring.clone();innerRing.geometry=new THREE.TorusGeometry(1.62,.012,6,64);
  innerRing.material=ring.material.clone();innerRing.material.color.set('#ffd83d');innerRing.material.opacity=.46;
  root.add(base,top,ring,innerRing);
  return root;
}

export class GaragePreview {
  constructor(camera,input,environment){
    this.camera=camera;
    this.input=input;
    this.scene=new THREE.Scene();
    this.scene.background=new THREE.Color('#131122');
    this.scene.environment=environment;
    this.effects=new EffectsSystem(this.scene);
    this.car=null;
    this.marker=null;
    this.carId='muscle';
    this.loadout=null;
    this.slot='wheelId';
    this.time=0;
    this.boostClock=0;
    this.explosionClock=0;
    this.cannonShotClock=0;
    this.previewProjectiles=[];
    this.pendingShotSound=null;
    this.yaw=CAMERA_PRESETS.wheelId.yaw;
    this.pitch=CAMERA_PRESETS.wheelId.pitch;
    this.distance=CAMERA_PRESETS.wheelId.distance;
    this.targetYaw=this.yaw;
    this.targetPitch=this.pitch;
    this.targetDistance=this.distance;
    this.focus=new THREE.Vector3(0,.58,0);
    this.setupScene();
  }

  setupScene(){
    const hemisphere=new THREE.HemisphereLight(0xdaf8ff,0x33243e,2.15);
    const key=new THREE.DirectionalLight(0xffefd5,4.2);
    key.position.set(-4,7,-5);key.castShadow=true;
    key.shadow.mapSize.set(1024,1024);
    key.shadow.camera.left=-4;key.shadow.camera.right=4;key.shadow.camera.top=4;key.shadow.camera.bottom=-4;
    const cyan=new THREE.PointLight(0x39d9ee,8,9);cyan.position.set(3,2.4,2.5);
    const pink=new THREE.PointLight(0xff5d91,5,8);pink.position.set(-3,1.4,2);
    this.scene.add(hemisphere,key,cyan,pink,createPedestal());
  }

  show(carId,loadout,slot='wheelId'){
    this.time=0;
    this.setSelection(carId,loadout,slot,true);
  }

  hide(){
    this.clearSelection();
  }

  clearSelection(){
    if(this.car)this.scene.remove(this.car);
    this.previewProjectiles.forEach((projectile)=>this.scene.remove(projectile.root));
    this.previewProjectiles.length=0;
    this.pendingShotSound=null;
    this.car=null;
    this.marker=null;
    this.effects.clear();
  }

  setSelection(carId,loadout,slot=this.slot,forcePreset=false){
    const slotChanged=slot!==this.slot;
    this.carId=carId;
    this.loadout={...loadout};
    this.slot=slot;
    this.clearSelection();

    if(slot==='tombstoneId'){
      this.marker=this.effects.placeTombstone(
        new THREE.Vector3(0,.025,0),
        getTombstone(loadout.tombstoneId),
        getCarPaintFinish(carId),
      );
      this.marker.rotation.y=0;
      this.marker.scale.setScalar(1.65);
      setShadows(this.marker);
      this.focus.set(0,.7,0);
    }else if(slot==='explosionId'){
      this.focus.set(0,.48,0);
    }else{
      this.car=createCar(carId,{scale:2.18,loadout});
      this.car.position.y=.055;
      this.car.rotation.y=0;
      setShadows(this.car);
      this.scene.add(this.car);
      this.focus.set(0,.62,0);
    }

    this.boostClock=0;
    this.explosionClock=0;
    this.cannonShotClock=0;
    if(slotChanged||forcePreset)this.applyCameraPreset(slot);
  }

  applyCameraPreset(slot=this.slot){
    const preset=CAMERA_PRESETS[slot]||CAMERA_PRESETS.wheelId;
    this.targetYaw=preset.yaw;
    this.targetPitch=preset.pitch;
    this.targetDistance=preset.distance;
  }

  orbit(deltaX,deltaY){
    this.targetYaw-=deltaX*.007;
    this.targetPitch=THREE.MathUtils.clamp(this.targetPitch-deltaY*.006,.06,.78);
  }

  zoom(delta){
    const preset=CAMERA_PRESETS[this.slot]||CAMERA_PRESETS.wheelId;
    this.targetDistance=THREE.MathUtils.clamp(this.targetDistance+delta*.003,preset.distance*.68,preset.distance*1.38);
  }

  resetCamera(){
    this.applyCameraPreset();
  }

  update(dt){
    this.time+=dt;
    const stick=this.input.cameraStick();
    if(Math.abs(stick.x)>.001||Math.abs(stick.y)>.001){
      this.targetYaw-=stick.x*dt*2.35;
      this.targetPitch=THREE.MathUtils.clamp(this.targetPitch-stick.y*dt*1.45,.06,.78);
    }
    const cameraBlend=1-Math.exp(-dt*9);
    this.yaw=THREE.MathUtils.lerp(this.yaw,this.targetYaw,cameraBlend);
    this.pitch=THREE.MathUtils.lerp(this.pitch,this.targetPitch,cameraBlend);
    this.distance=THREE.MathUtils.lerp(this.distance,this.targetDistance,cameraBlend);

    const horizontal=Math.cos(this.pitch)*this.distance;
    const desired=new THREE.Vector3(
      Math.sin(this.yaw)*horizontal,
      this.focus.y+Math.sin(this.pitch)*this.distance,
      Math.cos(this.yaw)*horizontal,
    );
    this.camera.position.lerp(desired,1-Math.exp(-dt*12));
    this.cameraTargetLook();

    const nitroActive=this.slot==='nitroId';
    if(this.car){
      updateCarVisual(this.car,{
        forwardSpeed:nitroActive?10:0,
        steer:0,
        boosting:nitroActive,
        grounded:true,
        distance:this.time*(nitroActive?10:0),
        groundHeight:0,
      },dt);
      if(nitroActive){
        this.boostClock-=dt;
        if(this.boostClock<=0){
          const nitro=this.car.userData.nitroEffect;
          const particleLife=Number(nitro.trailLength??1.55)/Math.max(.1,Number(nitro.speed??3));
          this.boostClock=THREE.MathUtils.clamp(
            particleLife/Math.max(1,Number(nitro.particleCount??30)),
            .008,
            .08,
          );
          this.effects.vehicleBoostSpark(this.car);
        }
      }
      if(this.slot==='projectileId'){
        this.cannonShotClock-=dt;
        if(this.cannonShotClock<=0){
          this.cannonShotClock=.85;
          this.firePreviewCannon();
        }
      }
    }

    this.updatePreviewProjectiles(dt);

    if(this.slot==='explosionId'){
      this.explosionClock-=dt;
      if(this.explosionClock<=0){
        const explosion=getDeathExplosion(this.loadout.explosionId);
        this.explosionClock=Math.max(.3,Number(explosion.duration??.95)+Number(explosion.loopDelay??.6));
        this.effects.elimination(new THREE.Vector3(0,.22,0),explosion);
      }
    }
    if(this.marker)this.marker.rotation.y=Math.sin(this.time*.45)*.08;
    this.effects.update(dt);
  }

  firePreviewCannon(){
    if(!this.car)return;
    const design=getCannon(this.loadout.cannonId);
    const projectile=getProjectile(this.loadout.projectileId||design.defaultProjectileId);
    const cannon=this.car.userData.cannon;
    const muzzleZ=Number(cannon.userData.muzzleZ??-.79);
    const muzzleY=Number(cannon.userData.muzzleY??.03);
    const muzzle=cannon.localToWorld(new THREE.Vector3(0,muzzleY,muzzleZ));
    const direction=new THREE.Vector3(0,0,-1).applyQuaternion(this.car.getWorldQuaternion(new THREE.Quaternion())).normalize();
    const root=createProjectileModel(projectile.id,this.car.userData.paintFinish);
    // Conserva exactamente la misma proporción proyectil/auto que durante
    // una partida. El auto del Garage es más grande, por lo que ambos usan
    // el mismo modelScale de esta instancia de preview.
    root.scale.multiplyScalar(this.car.userData.modelScale);
    root.position.copy(muzzle);
    root.quaternion.setFromUnitVectors(new THREE.Vector3(0,0,-1),direction);
    this.scene.add(root);
    this.previewProjectiles.push({root,velocity:direction.multiplyScalar(2.8),life:1.6,smokeClock:0,smokeColor:projectile.accent});
    this.effects.burst(muzzle,design.accent,3,1.25);
    this.pendingShotSound={cannon:design,projectile};
  }

  consumeShotSound(){
    const event=this.pendingShotSound;
    this.pendingShotSound=null;
    return event;
  }

  updatePreviewProjectiles(dt){
    for(let index=this.previewProjectiles.length-1;index>=0;index-=1){
      const projectile=this.previewProjectiles[index];
      projectile.life-=dt;
      projectile.root.position.addScaledVector(projectile.velocity,dt);
      projectile.root.rotateZ(dt*(projectile.root.userData.spin||8));
      projectile.smokeClock-=dt;
      if(projectile.smokeClock<=0){
        projectile.smokeClock=.045;
        const smokePosition=projectile.root.position.clone().addScaledVector(projectile.velocity,-.08);
        this.effects.projectileSmoke(smokePosition,projectile.smokeColor,1.1);
      }
      if(projectile.life<=0){
        this.scene.remove(projectile.root);
        this.previewProjectiles.splice(index,1);
      }
    }
  }

  cameraTargetLook(){
    this.camera.lookAt(this.focus);
    this.camera.fov=42;
    this.camera.updateProjectionMatrix();
  }
}
